'use client'
import { apiVerifyCaptcha } from '@/apis/captcha';
import { apiSubscribeNewsletter } from '@/apis/page_optimization';
import { faPaperPlane, FontAwesomeIcon } from '@/constants/icons';
import React, { useState } from 'react'
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface Props {
    companyId: string;
    domain: string;
}

const FooterNewsletter = ({ companyId, domain }: Props) => {
    const [email, setEmail] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [status, setStatus] = useState<'idle' | 'verifying' | 'sending' | 'failed'>('idle');
    const { executeRecaptcha } = useGoogleReCaptcha();

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleRate = async (e: React.FormEvent, p_email: string) => {
        e.preventDefault();

        if (!validateEmail(p_email)) {
            setError("Please enter a valid email address.");
            toast.error("Please enter a valid email address.");
            return;
        }
        setError(""); // Clear previous error if valid
        await handleSubmit(p_email);
    };

    const handleSubmit = async (p_email: string) => {
        try {
            if (!executeRecaptcha) {
                toast.error('reCAPTCHA not ready. Please try again.', { autoClose: 2000 });
                return;
            }

            setStatus('verifying');
            const recaptchaToken = await executeRecaptcha('newsletter_form');
            const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);

            if (!captchaRes?.verified) {
                setStatus('failed');
                toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
                return;
            }

            setStatus('sending');
            const response = await apiSubscribeNewsletter(companyId, p_email);
            if (response.message == "Subscribed successfully") {
                toast.success("Thank you for your feedback!", { autoClose: 2000 });
                setEmail("");
            } else {
                toast.error("You have already subscribed to our newsletter.")
            }
        } catch (error) {
            console.error("Error subscribing:", error);
            toast.error("There was an error subscribing. Please try again later.");
        } finally {
            setStatus('idle');
        }
    };

    return (
        <div className="w-full">
            <h3 className="relative mb-7 text-[13px] font-black text-white after:absolute after:-bottom-3 after:left-0 after:h-0.5 after:w-7 after:bg-[#ff7a00]">Newsletter</h3>
            <p className="mb-5 text-[13px] leading-6 text-[#fff]">
                Subscribe to get the latest deals & updates.
            </p>
            <form className="relative w-full" onSubmit={(e) => {handleRate(e, email)}} autoComplete="off">
                <div className="flex h-12 w-full items-stretch overflow-hidden rounded-lg border border-white/10 bg-[#101f3d] transition-all duration-300 focus-within:border-[#ff7a00]">
                    <input 
                        type="email" 
                        placeholder="Enter your email" 
                        className="min-w-0 flex-1 border-none bg-transparent px-4 text-[13px] text-white outline-none placeholder:text-[#9aa1ad] focus:ring-0"  
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        disabled={status !== 'idle'}
                    />
                    <button  
                        type="submit" 
                        disabled={status !== 'idle'}
                        className="flex w-12 shrink-0 items-center justify-center bg-[#ff7a00] text-white transition-colors duration-300 hover:bg-[#f23017] disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={status === 'idle' ? 'Subscribe' : status === 'verifying' ? 'Verifying' : 'Subscribing'}
                    >
                         <FontAwesomeIcon icon={faPaperPlane} className="h-4 w-4 transition-transform" />
                    </button>
                </div>
                {error && <p className="text-red-400 text-xs mt-3 ml-2">{error}</p>}
            </form>
        </div>
    )
}

export default FooterNewsletter

